'use client';

/**
 * Cray26 Theme Footer Component
 * Custom footer for cray26 theme
 */
import { use, useEffect, useState } from 'react';

import { slugifyTitle } from '@/lib/helpers';
import { type IAddress } from '@/services';

import CopyRight from './CopyRight';
import DataWebCrayon from './DataWeb';
import EmailList from './EmailList';
import FAQLinkList from './FAQLinkList';
import LocationList from './LocationList';
import NewsLetter from './NewsLetter';
import PhoneList from './PhoneList';
import SocialLinkList from './SocialLinkList';
import WhatsAppButton from './WhatsAppButton';

type FooterProps = {
  emails?: string[];
  phones?: string[];
  faqs?: { question: string; link: string }[];
  addresses?: IAddress[];
  socialLinks?: { platform: string; url: string }[];
  mocked?: boolean;
  whatsapp?: any;
  whatsappMessage?: string;
};

export const Footer = (props: FooterProps) => {
  const [footerData, setFooterData] = useState<FooterProps>({});

  // Mock data for demonstration purposes
  const mocks = {
    emails: ['info@crayon.com', 'support@crayon.com'],
    phones: ['+1 234 567 890', '+1 987 654 321'],
    faqs: [
      { question: '¿Cómo puedo hacer un pedido?', link: `/faq-post/${slugifyTitle('¿Cómo puedo hacer un pedido?')}` },
      { question: '¿Cuál es la política de devoluciones?', link: `/faq-post/${slugifyTitle('como-puedo-cambiar-devolver-un-producto')}` },
      { question: '¿Cómo rastrear mi envío?', link: `/faq-post/${slugifyTitle('¿Cómo rastrear mi envío?')}` },
      { question: '¿Qué métodos de pago se aceptan?', link: `/faq-post/${slugifyTitle('¿Qué métodos de pago se aceptan?')}` },
      { question: '¿Cómo puedo contactar al soporte?', link: `/faq-post/${slugifyTitle('¿Cómo puedo contactar al soporte?')}` },
      { question: '¿Dónde están ubicados sus locales?', link: `/faq-post/${slugifyTitle('¿Dónde están ubicados sus locales?')}` },
    ],
    locations: [
      'Duarte Quirós 1400. Nuevocentro Shopping. Local 1168. Nivel Uno. Bº Alberdi',
      'Av. Padre Luchesse esq. Mendoza. VAS. Local: 230-231. Villa Allende.',
      'Rodríguez del Busto 4086. Dinosaurio Mall Alto Verde. Local 110. Bº Alto Verde.',
      'Av. Sabattini 3700. Portal Shopping. Local 2083. Nivel 2. Bº Argüello.',
      'Av. Rafael Nuñez 4200. Nuevocentro Shopping. Local 2133. Nivel Dos. Bº Cerro de las Rosas.',
      'Av. Ejército Argentino 1550. Shopping Patio Olmos. Local 1081. Nivel 1. Bº Centro.',
    ],
    socialLinks: [
      { platform: 'facebook', url: 'https://facebook.com/crayon' },
      { platform: 'twitter', url: 'https://twitter.com/crayon' },
    ],
    whatsapp: '+1234567890',
    whatsappMessage: '¡Hola! ¿En qué puedo ayudarte?',
  };

  useEffect(() => {
    if (props.mocked) {
      setFooterData(mocks);
    } else {
      setFooterData({
        emails: props.emails,
        phones: props.phones,
        addresses: props.addresses,
        socialLinks: props.socialLinks,
        whatsapp: props.whatsapp,
        whatsappMessage: props.whatsappMessage,
        faqs: [
          { question: '¿Cómo puedo hacer un pedido?', link: `/faq-post/${slugifyTitle('¿Cómo puedo hacer un pedido?')}` },
          { question: '¿Cuál es la política de devoluciones?', link: `/faq-post/${slugifyTitle('¿Cuál es la política de devoluciones?')}` },
          { question: '¿Cómo rastrear mi envío?', link: `/faq-post/${slugifyTitle('¿Cómo rastrear mi envío?')}` },
          { question: '¿Qué métodos de pago se aceptan?', link: `/faq-post/${slugifyTitle('¿Qué métodos de pago se aceptan?')}` },
          { question: '¿Cómo puedo contactar al soporte?', link: `/faq-post/${slugifyTitle('¿Cómo puedo contactar al soporte?')}` },
          { question: '¿Dónde están ubicados sus locales?', link: `/faq-post/${slugifyTitle('¿Dónde están ubicados sus locales?')}` },
        ],
      });
    }
  }, [props.mocked, props.emails, props.phones, props.faqs, props.addresses, props.socialLinks, props.whatsapp, props.whatsappMessage]);

  return (
    <>
      <footer className="cray26-footer px-12 pt-12 relative flex flex-col bg-secondary-800 text-white text-sm">
        <div className="md:pt-theme-3xl md:px-theme-3xl  flex md:flex-row flex-col md:gap-theme-md gap-theme-md">
          <div className="flex-auto grid md:grid-cols-2 grid-cols-1 md:gap-theme-lg gap-theme-md">
            <EmailList emails={footerData.emails || []} title={'Información de contacto'} />
            <PhoneList phones={footerData.phones || []} title={'Teléfonos'} />
            <FAQLinkList faqs={footerData.faqs || []} title={'Preguntas Frecuentes'} className="md:col-span-2" />
          </div>
          <div className="">
            <LocationList locations={footerData.addresses || []} title="Nuestros locales exclusivos" />
          </div>
        </div>
        <div className="flex flex-row md:pt-theme-lg pt-theme-md md:px-theme-3xl  items-start justify-between gap-theme-lg flex-wrap">
          <div>
            <SocialLinkList className="flex-1" links={footerData.socialLinks || []} title={'Seguínos en nuestras redes'} />
          </div>
          <div className='flex flex-row items-center gap-10'>
            <DataWebCrayon />
            <NewsLetter className="w-75 md:w-100" />
          </div>
        </div>
        <div>
          <CopyRight />
        </div>
      </footer>
      <WhatsAppButton size="lg" phoneNumber={footerData.whatsapp} message={footerData.whatsappMessage} />
    </>
  );
};

export default Footer;
